// CH6a blind-relay client. Opens a rendezvous, pushes opaque frames toward the
// peer's mailbox, and long-polls its own. The server relays ciphertext only —
// see backend/internal/chat/linking.go and docs/specs/chat-device-linking.md.

import { ChatApiError, fromB64, toB64 } from './chatApi';

/** Rendezvous sides: 'p' = primary (QR shower / creator), 'c' = companion
 * (scanner). Each side writes to the peer's box and reads its own. */
export type LinkSide = 'p' | 'c';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ChatApiError(res.status, message);
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const linkingApi = {
  /** Primary opens a rendezvous scoped to its own account. */
  create: () => post<{ rendezvousId: string; ttlSeconds: number }>('/v1/chat/linking', {}),

  /** Push one opaque frame toward the peer. `side` is the CALLER's side. */
  send: (id: string, side: LinkSide, frame: Uint8Array) =>
    post<{ ok: boolean }>(`/v1/chat/linking/${encodeURIComponent(id)}/send`, {
      side,
      frame: toB64(frame),
    }),

  /** Long-poll the caller's own mailbox. Resolves to the next frame, or null on
   * a poll timeout — the caller simply calls again. */
  recv: async (id: string, side: LinkSide): Promise<Uint8Array | null> => {
    const { frame } = await request<{ frame: string | null }>(
      `/v1/chat/linking/${encodeURIComponent(id)}/recv?side=${side}`,
    );
    return frame ? fromB64(frame) : null;
  },

  close: (id: string) => post<{ ok: boolean }>(`/v1/chat/linking/${encodeURIComponent(id)}/close`, {}),
};
